import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { SkpButton, SkpText } from '@skeepers/skeepers-ui-react'
import { faTimes } from '@skeepers/skeepers-icons/solid/faTimes'
import { faPlus } from '@skeepers/skeepers-icons/solid/faPlus'
import { faPlay } from '@skeepers/skeepers-icons/solid/faPlay'

const DEFAULT_ACCEPT =
  '.jpg, .jpeg, .jp2, .jpx, .gif, .png, .webp, .mp4, .mov, .qt'

const VIDEO_EXTENSIONS = /\.(mp4|mov|qt)(\?.*)?$/i

function isVideoValue(value) {
  if (!value) return false
  if (typeof value !== 'string') return false
  if (value.startsWith('data:video/')) return true
  return VIDEO_EXTENSIONS.test(value)
}

export function MediaUploader({
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  maxSize = 25, // MB
  name,
  id,
  width = 300,
  height = 300,
  buttonSize = 'medium',
  error,
  disabled = false,
  label,
}) {
  const [uploadError, setUploadError] = useState()
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef(null)

  const setMedia = e => {
    const file = e.target.files?.[0]
    if (file) {
      const allowedExtensions = new RegExp(
        `(${accept.replace(/,\s/g, '|')})$`,
        'i'
      )
      if (!allowedExtensions.test(file.name)) {
        setUploadError(I18n.t('js.components.imageupload.errors.wrong_format'))
        return false
      }

      const reader = new FileReader()
      reader.onload = readerEvent => {
        onChange(readerEvent.target.result)
      }

      if (file.size > maxSize * 1024 * 1024) {
        setUploadError(I18n.t('js.components.imageupload.errors.too_heavy'))
      } else {
        setUploadError()
        reader.readAsDataURL(file)
      }
    }
  }

  const removeMedia = () => {
    onChange(null)
    setUploadError()
    setIsPlaying(false)
  }

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play()
    } else {
      video.pause()
    }
  }

  const displayError = error || uploadError

  if (value) {
    const valueIsVideo = isVideoValue(value)

    return (
      <div>
        <Preview width={width} height={height}>
          {valueIsVideo ? (
            <VideoWrap onClick={togglePlay}>
              <video
                ref={videoRef}
                src={value}
                width={width}
                height={height}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
              {!isPlaying && (
                <PlayOverlay>
                  <PlayCircle>
                    <SkpButton
                      type="secondary"
                      icon={faPlay}
                      text=""
                      size={buttonSize}
                    />
                  </PlayCircle>
                </PlayOverlay>
              )}
            </VideoWrap>
          ) : (
            <img src={value} width={width} height={height} alt="" />
          )}
          <RemoveButtonWrap>
            <SkpButton
              type="secondary"
              icon={faTimes}
              text=""
              size="small"
              onClick={removeMedia}
            />
          </RemoveButtonWrap>
        </Preview>

        {displayError && (
          <SkpText
            skpClass="mt-8"
            text={displayError}
            size="small"
            color="--skp-color-error-600"
          />
        )}
      </div>
    )
  }

  return (
    <div>
      <Uploader displayError={displayError} width={width} height={height}>
        {label && (
          <SkpText text={label} size="small" color="--skp-color-neutral-700" />
        )}
        <SkpButton
          type="primary"
          icon={faPlus}
          text=""
          size={buttonSize}
          disabled={disabled}
        />
        <input
          disabled={disabled}
          type="file"
          accept={accept}
          onChange={setMedia}
          name={name}
          id={id || name}
          title=""
        />
      </Uploader>

      {displayError && (
        <SkpText
          skpClass="mt-8"
          text={displayError}
          size="small"
          color="--skp-color-error-600"
        />
      )}
    </div>
  )
}

const Uploader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  ${({ width, height }) => `
    width: ${width}px;
    height: ${height}px;
  `}
  border: 2px dashed var(--skp-color-neutral-400);
  border-radius: 8px;

  input {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    opacity: 0;
    cursor: pointer;

    &:focus {
      outline: none;
    }
  }
`

const Preview = styled.div`
  position: relative;
  ${({ width, height }) => `
    width: ${width}px;
    height: ${height}px;
  `}

  img,
  video {
    border-radius: 8px;
    object-fit: cover;
    object-position: center;
  }
`

const VideoWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
`

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const PlayCircle = styled.div`
  border-radius: 50%;
  overflow: hidden;
  pointer-events: none;
`

const RemoveButtonWrap = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`
